<template>
    <div>
        <span class="block text-sm font-medium text-gray-700">
            Share a photo or video
            <span class="font-normal text-gray-500">(optional)</span>
        </span>

        <ReviewPhotoCapture
            ref="photoCapture"
            :account-hash="accountHash"
            :csrf="csrf"
            :initial-ids="initialIds"
            :maximum="maximumPhotos"
            :thumbnail-variant="thumbnailVariant"
            @camera-available="webcamAvailable = $event"
            @changed="emit('photosChanged', $event)"
            @at-capacity="photosAtCapacity = $event"
            @uploading="emit('photosUploading', $event)"
        />

        <ReviewVideoCapture
            ref="videoCapture"
            :allowed="videoAllowed"
            :clip-seconds="videoClipSeconds"
            :csrf="csrf"
            :initial-id="initialVideoId"
            :maximum-bytes="maximumVideoBytes"
            @changed="emit('videoChanged', $event)"
            @at-capacity="videoAtCapacity = $event"
            @uploading="emit('videoUploading', $event)"
        />

        <p
            v-if="refusal"
            data-test="media-refused"
            class="mt-2 text-sm text-red-600"
        >
            {{ refusal }}
        </p>

        <div class="mt-2 flex flex-wrap items-center gap-3">
            <input
                ref="filePicker"
                name="media_file"
                type="file"
                accept="image/jpeg,image/png,video/*"
                multiple
                class="sr-only"
                @change="onFilesPicked"
            />

            <!-- capture="user" opens the front camera outright on Android and offers it in the
                 iOS picker. It is the whole recording route for a browser with no MediaRecorder,
                 so it stays on an input of its own rather than sharing the picker above, whose
                 accept list would make the phone offer the photo library instead. -->
            <input
                ref="cameraPicker"
                name="video_file"
                type="file"
                accept="video/*"
                capture="user"
                class="sr-only"
                @change="onCameraFilePicked"
            />

            <button
                type="button"
                data-test="choose-media"
                :disabled="photosAtCapacity && videoAtCapacity"
                class="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                @click="filePicker?.click()"
            >
                <PhotographIcon class="mr-2 h-5 w-5" aria-hidden="true" />
                Choose a photo or video
            </button>

            <button
                v-if="webcamAvailable"
                type="button"
                data-test="open-webcam"
                :disabled="photosAtCapacity || openingCamera"
                class="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                @click="takeAPhoto"
            >
                <CameraIcon class="mr-2 h-5 w-5" aria-hidden="true" />
                Take a photo
            </button>

            <button
                type="button"
                data-test="record-video"
                :disabled="videoAtCapacity || openingCamera"
                class="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                @click="recordAVideo"
            >
                <VideoCameraIcon class="mr-2 h-5 w-5" aria-hidden="true" />
                Record a video
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
/**
 * One heading and one row of controls over the photo strip and the video the review carries.
 *
 * The two capture components keep their own state and their own dialogs; what lives here is the
 * chrome they share and the routing of a picked file to whichever of them it belongs to.
 */
import { ref } from "vue";
import {
    CameraIcon,
    PhotographIcon,
    VideoCameraIcon,
} from "@heroicons/vue/solid";
import ReviewPhotoCapture from "./ReviewPhotoCapture.vue";
import ReviewVideoCapture from "./ReviewVideoCapture.vue";

defineProps<{
    accountHash?: string;
    csrf?: string;
    initialIds?: string[];
    initialVideoId?: string;
    maximumPhotos?: number;
    maximumVideoBytes?: number;
    thumbnailVariant?: string;
    videoAllowed?: boolean;
    videoClipSeconds?: number;
}>();

const emit = defineEmits<{
    (event: "photosChanged", ids: string[]): void;
    (event: "photosUploading", uploading: boolean): void;
    (event: "videoChanged", id: string | null): void;
    (event: "videoUploading", uploading: boolean): void;
}>();

const photoCapture = ref<InstanceType<typeof ReviewPhotoCapture> | null>(null);
const videoCapture = ref<InstanceType<typeof ReviewVideoCapture> | null>(null);
const filePicker = ref<HTMLInputElement | null>(null);
const cameraPicker = ref<HTMLInputElement | null>(null);

const refusal = ref("");
const recordingInThePage = ref(true);
const webcamAvailable = ref(false);
const photosAtCapacity = ref(false);
const videoAtCapacity = ref(false);
const openingCamera = ref(false);

/**
 * Send each picked file to the flow it belongs to, so a customer who selects three photos and a
 * clip in one go gets all four.
 *
 * A file that is neither is answered here rather than pushed into one of the flows to fail there:
 * `accept` is advisory and a customer can pick past it, and "that file could not be read as a
 * video" reads as a fault in a file the section simply does not take.
 */
function onFilesPicked(event: Event): void {
    const files = filesPickedIn(event);
    const images = files.filter(isAnImage);
    const clips = files.filter(isAVideo);

    refusal.value =
        images.length + clips.length < files.length
            ? "Only photos and videos can be added to a review."
            : "";

    photoCapture.value?.addFiles(images);
    videoCapture.value?.addFiles(clips);
}

function onCameraFilePicked(event: Event): void {
    refusal.value = "";

    videoCapture.value?.addFiles(filesPickedIn(event));
}

/**
 * The files a picker handed over, clearing it so picking the same file twice running still fires
 * `change`.
 */
function filesPickedIn(event: Event): File[] {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);

    input.value = "";

    return files;
}

/**
 * Which flow a file belongs to, by the type the browser reports rather than by its extension.
 */
function isAnImage(file: File): boolean {
    return file.type.startsWith("image/");
}

function isAVideo(file: File): boolean {
    return file.type.startsWith("video/");
}

async function takeAPhoto(): Promise<void> {
    openingCamera.value = true;

    try {
        await photoCapture.value?.openWebcam();
    } finally {
        openingCamera.value = false;
    }
}

/**
 * Record in the page where that works, and open the phone's own camera where it does not.
 *
 * A browser that cannot record answers before any prompt, so the picker still opens on the press
 * that asked for it. A *declined* camera answers only once the customer has dealt with the
 * permission dialog, by which point Safari no longer counts this as a click of theirs and can
 * refuse to open the picker — so the answer is remembered, and the next press goes straight there.
 */
async function recordAVideo(): Promise<void> {
    if (!recordingInThePage.value) {
        cameraPicker.value?.click();

        return;
    }

    openingCamera.value = true;

    try {
        if (!(await videoCapture.value?.openRecorder())) {
            recordingInThePage.value = false;
            cameraPicker.value?.click();
        }
    } finally {
        openingCamera.value = false;
    }
}
</script>
